/**
 * Use cases — organised by the situation someone arrives in.
 * @file src/web/features/usecases/data/cases.ts
 *
 * Deliberately not industries: the infrastructure a clinic needs and the
 * infrastructure a warehouse needs are identical, so sector pages end up as
 * one template with the noun swapped. What genuinely differs is the state
 * things are in when someone calls — and that changes what we do first.
 */

export interface UseCase {
  slug: string;
  eyebrow: string;
  title: string;
  /** Search-shaped title for <title>; the on-page H1 stays conversational. */
  metaTitle: string;
  keywords: string;
  description: string;
  intro: string;
  /** How someone recognises themselves in this page. */
  signs: string[];
  /** What actually happens, and in what order. Different per case. */
  firstWeek: { title: string; body: string }[];
  /** What it costs to leave it as-is. */
  risk: { title: string; body: string };
  closing: string;
  /** Answers to what people actually search. Also emitted as FAQPage JSON-LD. */
  faqs: { q: string; a: string }[];
}

export const useCases: UseCase[] = [
  {
    slug: 'developer-left',
    eyebrow: 'Handover',
    title: 'Your developer left and nobody knows the passwords',
    metaTitle: 'Developer Left? Recover Your Server and Domain',
    keywords: 'developer left company, lost server access, recover domain access, lost hosting password, website access after employee leaves',
    description:
      'The person who set everything up has gone. Nothing is broken yet — but nobody can log in to fix it when it is.',
    intro:
      'This is the most common way a business discovers it has an infrastructure problem: not through an outage, but through a resignation.',
    signs: [
      'Nobody is certain which provider the servers are actually on',
      'The domain renews to a personal email address of someone who has left',
      'There is one admin account and the password is in a message thread',
      'No one can say when the last backup was tested, or whether backups exist',
    ],
    firstWeek: [
      {
        title: 'We find out what exists',
        body: 'Before anything changes, we inventory it: which servers, which provider, which domains, which certificates, what renews when and on whose card. Most businesses in this position have never seen the full list.',
      },
      {
        title: 'We recover access',
        body: 'Registrar, hosting, DNS, database, mail. Where accounts are tied to a departed individual, we work through the provider recovery routes. This is the slow part, and it is the part that gets harder the longer you wait.',
      },
      {
        title: 'We move ownership to the business',
        body: 'Accounts move to company addresses, billing moves to a company card, and access is issued per person rather than shared. Losing one more employee stops being an event.',
      },
      {
        title: 'We take over the running',
        body: 'Patching, renewals, backups and monitoring become ours. Your team keeps day-to-day control of DNS and certificates through the console.',
      },
    ],
    risk: {
      title: 'Why waiting makes it harder',
      body: 'Account recovery depends on evidence of ownership, and that evidence decays. A former employee’s mailbox gets closed, a card expires, a phone number is reassigned. The window where recovery is a support ticket rather than a legal exercise closes quietly.',
    },
    closing:
      'Tell us what you know and what you have lost access to. We will tell you what is recoverable and how long it takes.',
    faqs: [
      {
        q: 'How do I regain access to a server when the developer has left?',
        a: 'Start with the provider, not the server. Hosting companies and registrars have account-recovery routes that rely on proving ownership — company registration, the billing card, or the original signup email. Recovering the account gets you the server; trying to break into the server rarely does.',
      },
      {
        q: 'What if the domain is registered in an ex-employee\'s name?',
        a: 'Registrars run a formal transfer-of-ownership process. It needs documentation showing the business owns the name, and it is far easier while the registration is current. Once a domain expires it enters redemption, where recovery costs considerably more and can fail entirely.',
      },
      {
        q: 'Can you take over without any handover from the person who left?',
        a: 'Usually yes. Most of what is needed can be established from the outside — where DNS points, who the registrar is, which certificates are live. Cooperation makes it faster, but it is not a precondition.',
      },
      {
        q: 'How long does recovering access take?',
        a: 'Anywhere from a day to a few weeks, depending entirely on how the accounts were registered. Provider recovery is the slow step, and it gets slower as evidence of ownership ages.',
      },
    ],
  },

  {
    slug: 'untested-backups',
    eyebrow: 'Recovery',
    title: 'You have backups. Nobody has ever restored one.',
    metaTitle: 'Are Your Backups Actually Restorable?',
    keywords: 'test database backup, restore backup, backup verification, backup failed silently, database recovery plan',
    description:
      'A backup job that reports success is not the same as a file you can restore from. The difference only becomes visible on the day it matters.',
    intro:
      'Almost every business we take over has backups of some kind. Far fewer have ever restored one, and a meaningful number turn out not to have what they think they have.',
    signs: [
      'Nobody can say when a backup was last restored, only when one last ran',
      'The backup reports success, and nobody reads the report',
      'Retention is whatever the provider set by default, and nobody chose it',
      'The backup lives on the same server, or the same account, as the thing it protects',
    ],
    firstWeek: [
      {
        title: 'We find out what is actually being captured',
        body: 'Which databases, which files, on what schedule, to where. It is common to find something excluded — a database created after the job was written, or one the backup user cannot read. A run that skips a database and reports success is the normal failure, not a dramatic one.',
      },
      {
        title: 'We restore one, for real',
        body: 'Into a scratch environment, from the newest backup, and we count the rows. This is the only test that means anything: everything else is a report about a file, not a check of what is inside it.',
      },
      {
        title: 'We fix what the restore exposes',
        body: 'Missing databases, permissions the dump role never had, retention too short to survive a problem discovered on Monday, backups stored where a compromise of the server would take them too.',
      },
      {
        title: 'We make failure loud',
        body: 'Nightly dumps to offsite object storage, kept 30 days, with an alert when a database stops being captured. A backup that stops working should interrupt somebody, not wait to be noticed.',
      },
    ],
    risk: {
      title: 'How this fails quietly',
      body: 'Backup jobs fail one database at a time. The run still finishes, the summary still says most things worked, and the one that stopped is buried in a status file nobody opens. We found exactly this on our own fleet: a production database whose nightly dump had been refused for five consecutive nights because the backup role had lost permission to read it. Everything else in the run succeeded, so nothing ever raised its voice.',
    },
    closing:
      'Tell us where your backups go and when one was last restored. If the answer to the second is "never", that is the normal answer, and it is the one worth fixing first.',
    faqs: [
      {
        q: 'How often should database backups be tested?',
        a: 'A full restore at least quarterly, and immediately after any change to the schema, the backup user or the storage target. Between those, an automated check that each expected database produced a file of a plausible size catches the common silent failure.',
      },
      {
        q: 'Why would a backup job report success but miss a database?',
        a: 'Because most jobs report on the run rather than on each database. A permission the dump user lacks, a database created after the job was written, or a new security policy on a table will fail that one database while the others succeed — and the overall run still finishes.',
      },
      {
        q: 'How long should backups be kept?',
        a: 'Long enough to survive a problem you discover late. Thirty days is a reasonable default: it covers corruption noticed weeks after it started, which a seven-day window does not.',
      },
      {
        q: 'Do backups need to be stored somewhere else?',
        a: 'Yes. A backup on the same server protects you from deleting a table and from nothing else — not from the disk failing, the account being compromised, or the provider suspending it. Offsite means a different account, not only a different folder.',
      },
    ],
  },

  {
    slug: 'certificate-expired',
    eyebrow: 'Outage',
    title: 'Your certificate expired and the browser is shouting at visitors',
    metaTitle: 'SSL Certificate Expired? How to Stop It Recurring',
    keywords: 'ssl certificate expired, https not secure warning, certificate renewal failed, lets encrypt renewal, site says not secure',
    description:
      'Nothing was deployed, nothing broke, and the site now warns every visitor away. A certificate reached its date and no one was watching for it.',
    intro:
      'This is the outage with no cause: no release, no traffic spike, no failure. A date passed, and browsers started telling people your site is unsafe.',
    signs: [
      'Visitors see "Your connection is not private" and leave',
      'Nobody is sure which certificates exist, or what renews them',
      'A renewal cron exists but nobody has checked that it ran',
      'The certificate was renewed by hand last time, by someone who has since left',
    ],
    firstWeek: [
      {
        title: 'We get the site trusted again',
        body: 'A working certificate first, before anything else is diagnosed. Visitors seeing a warning is the emergency; understanding why comes second.',
      },
      {
        title: 'We find every certificate you have',
        body: 'Across every domain and subdomain, with expiry dates. Businesses routinely find certificates they did not know about, and subdomains nobody remembers standing up.',
      },
      {
        title: 'We make renewal automatic, and verify it',
        body: 'Automatic renewal is standard; silent failure is equally standard. Renewal needs the challenge path to be reachable, which an nginx rule added months later can quietly break. We check that the renewal actually completes, not that the job is installed.',
      },
      {
        title: 'We watch the dates',
        body: 'Expiry is tracked per domain and alerts well before the deadline. A certificate should never be able to expire without somebody having been told twice.',
      },
    ],
    risk: {
      title: 'Why this keeps happening',
      body: 'Automatic renewal usually works, and when it stops working it stops silently. The job runs, fails its challenge, exits, and writes to a log nobody reads — until 90 days later a browser makes the announcement for you. The failure is always weeks old by the time anyone hears about it.',
    },
    closing:
      'If your site is warning visitors right now, tell us the domain. Getting it trusted again is usually minutes, and stopping it recurring is the part worth talking about afterwards.',
    faqs: [
      {
        q: 'How do I fix an expired SSL certificate?',
        a: 'Issue a new one and load it. With Let\u2019s Encrypt that is a single renewal command once the domain still points at the server, and the site is trusted again as soon as the web server reloads. The more useful question is why the automatic renewal stopped.',
      },
      {
        q: 'Why did my certificate not renew automatically?',
        a: 'Most often because the validation challenge could not reach the server: a redirect rule, a firewall change, or a configuration edit that no longer serves the challenge path. The renewal job still runs on schedule, fails, and logs it somewhere nobody looks.',
      },
      {
        q: 'How much notice should I get before a certificate expires?',
        a: 'Weeks, not days. Let\u2019s Encrypt certificates last 90 days and renew at 60, which leaves 30 days of margin — enough to notice a broken renewal, but only if something is checking and telling you.',
      },
      {
        q: 'Does an expired certificate affect search rankings?',
        a: 'Indirectly and quickly. Search engines prefer HTTPS, but the immediate damage is behavioural: visitors meet a full-page browser warning and leave, and that bounce is measured.',
      },
    ],
  },

  {
    slug: 'agency-handover',
    eyebrow: 'Handover',
    title: 'The agency that built it has gone quiet',
    metaTitle: 'Moving Your Site Away From an Agency',
    keywords: 'move website from agency, agency not responding, website handover, transfer hosting from developer, agency owns my domain',
    description:
      'The build finished, the invoices stopped, and now replies take a fortnight. Your software still runs on their arrangements.',
    intro:
      'Agencies are built to deliver projects, not to operate them. When the project ends, maintenance becomes goodwill — and goodwill has a half-life.',
    signs: [
      'Support requests take days, and small changes get quoted as new work',
      'The hosting is on the agency’s account, billed through them',
      'You do not have access to the code, or you have a copy that may not be current',
      'Nobody has mentioned backups or updates since launch',
    ],
    firstWeek: [
      {
        title: 'We work out what is yours',
        body: 'Domain, code, data and hosting are often held in four different places under three different names. We establish what you own outright, what is under the agency’s account, and what needs to be formally transferred.',
      },
      {
        title: 'We take a full copy first',
        body: 'Before any migration conversation, we secure a complete copy of the application and its data. Handovers go wrong most often when the incumbent is unhelpful, so we stop depending on them early.',
      },
      {
        title: 'We migrate without downtime',
        body: 'The application moves to our infrastructure, DNS is cut over, certificates are issued in advance. Your site keeps serving throughout — the switch is a DNS change, not an outage window.',
      },
      {
        title: 'You keep the relationship you want',
        body: 'Plenty of businesses stay with their agency for development and move only the running to us. Those are different jobs, and they do not have to be the same supplier.',
      },
    ],
    risk: {
      title: 'The failure mode to avoid',
      body: 'Businesses in this position usually wait until something breaks, which is the worst moment to attempt a handover — you need cooperation from a party who is slow to respond, while your site is down. Moving while everything works is dramatically easier.',
    },
    closing:
      'You do not need the agency’s cooperation to start. Tell us what you have and we will map the rest.',
    faqs: [
      {
        q: 'The agency hosts our website. Can we move it without their help?',
        a: 'Almost always. The application and its data can be copied, and DNS is controlled by whoever holds the domain. The one genuine blocker is the domain itself being registered under the agency, which needs a transfer they have to authorise.',
      },
      {
        q: 'Will moving hosting take our site offline?',
        a: 'It should not. The application is set up and tested on the new infrastructure first, certificates are issued in advance, and the final step is a DNS change. Visitors move across as DNS propagates, with both copies serving during the overlap.',
      },
      {
        q: 'Do we own the code the agency wrote?',
        a: 'That depends on your contract, and it is worth checking before starting a migration. Many agency agreements assign ownership on final payment; some license it instead. Either way, take a complete copy early.',
      },
      {
        q: 'Can we keep the agency for development and move only the hosting?',
        a: 'Yes, and it is a common arrangement. Building software and operating infrastructure are different jobs with different rhythms, and they do not have to be the same supplier.',
      },
    ],
  },

  {
    slug: 'consolidate-providers',
    eyebrow: 'Cost',
    title: 'Five providers and nobody knows what you pay',
    metaTitle: 'Consolidate Hosting, Domains and Backups',
    keywords: 'consolidate hosting providers, reduce infrastructure costs, one bill hosting, too many cloud subscriptions, hosting cost audit',
    description:
      'Hosting here, domains there, backups somewhere else, and an invoice arriving from a service nobody recognises.',
    intro:
      'Nobody chooses vendor sprawl. It accumulates one urgent decision at a time, and by the time it is a problem it looks like normality.',
    signs: [
      'Infrastructure spend is spread across several cards and one personal account',
      'At least one subscription is for something nobody can identify',
      'Renewal dates are unknown until a payment fails',
      'When something breaks, each provider confirms their part is fine',
    ],
    firstWeek: [
      {
        title: 'We audit what you actually pay',
        body: 'Every provider, every renewal date, every amount, on one page. This alone usually finds something: a duplicate service, an oversized server, or a subscription still billing for a project that ended.',
      },
      {
        title: 'We tell you what to cancel',
        body: 'Including things you do not need us for. If a provider is doing a good job at a fair price, keeping them is the right answer and we will say so.',
      },
      {
        title: 'We consolidate what is left',
        body: 'Servers, domains, certificates, backups, storage, mail and monitoring move onto one plan with one invoice and one renewal date.',
      },
      {
        title: 'You see the whole estate in one place',
        body: 'Every domain and certificate with its real expiry date, read from the server rather than from what somebody recorded at setup.',
      },
    ],
    risk: {
      title: 'The cost that is not on the invoices',
      body: 'The subscriptions are the visible half. The other half is the time spent chasing which provider owns a problem, and the renewals that lapse because they belonged to nobody in particular.',
    },
    closing:
      'Send us your current invoices. We will show you the consolidated figure and what we would cancel outright.',
    faqs: [
      {
        q: 'How much does it cost to run a small web application?',
        a: 'Bought separately, a server, managed database, backups, object storage, domain, transactional email and uptime monitoring typically come to $70–90 a month. The larger cost is usually the time someone spends keeping them working, which never appears on an invoice.',
      },
      {
        q: 'Is bundling infrastructure actually cheaper than buying it separately?',
        a: 'Generally yes, because the provider buys capacity at scale — but the bigger saving is the work. Consolidation also removes the failure mode where a renewal lapses because it belonged to nobody in particular.',
      },
      {
        q: 'We do not know what we are paying for. Where do we start?',
        a: 'With an audit: every provider, every renewal date, every amount on one page. Card statements are the fastest route, since forgotten subscriptions rarely send invoices anyone reads.',
      },
      {
        q: 'What if some of our current providers are fine?',
        a: 'Then keep them. Consolidation is worth doing where it removes work or risk, not as an end in itself. We will say plainly which of your existing vendors we would leave alone.',
      },
    ],
  },

  {
    slug: 'launching-without-ops',
    eyebrow: 'Launch',
    title: 'You are launching and do not want to hire ops',
    metaTitle: 'Launch Without Hiring DevOps',
    keywords: 'launch without devops, managed infrastructure setup, do i need a devops engineer, server setup for startup, outsource server management',
    description:
      'The application is nearly ready. Everything underneath it still has to exist, and none of it is what your team was hired for.',
    intro:
      'Greenfield is the easiest time to get this right, because there is nothing to unpick — and the hardest time to justify, because nothing has gone wrong yet.',
    signs: [
      'Your developers are strong on the product and thin on infrastructure',
      'Launch is dated and server setup is not on anybody’s list',
      'You are weighing a DevOps hire against shipping features',
      'Nobody has decided who gets called if the site goes down at midnight',
    ],
    firstWeek: [
      {
        title: 'We set the environment up',
        body: 'Servers sized to what you expect, database provisioned, storage attached, staging alongside production. Ready before launch rather than during it.',
      },
      {
        title: 'Domains and certificates before you need them',
        body: 'DNS configured, certificates issued and renewal verified. Launch day is a bad time to discover a certificate takes an hour to propagate.',
      },
      {
        title: 'Backups and monitoring from day one',
        body: 'Backups run and are restore-tested before there is data worth losing. Monitoring starts before the first customer, not after the first outage.',
      },
      {
        title: 'Your team ships, we operate',
        body: 'Deployments stay in your developers’ hands. Everything below the application is ours, and stays ours as you grow.',
      },
    ],
    risk: {
      title: 'What a hire actually costs',
      body: 'For most businesses at launch the infrastructure work is a few hours a month, spread unpredictably. That does not fit a full-time role, so the work gets squeezed out by whatever is louder — and the gaps only appear when something breaks.',
    },
    closing:
      'Tell us your launch date and what you are building. We will have the infrastructure ready before it.',
    faqs: [
      {
        q: 'Do we need a DevOps engineer to launch?',
        a: 'For most launches, no. The infrastructure work is real but small — a few hours a month, spread unpredictably. That shape does not fit a full-time role, and the work tends to get squeezed out by whatever is louder that week.',
      },
      {
        q: 'What infrastructure does a new web application actually need?',
        a: 'A server, a database, storage for uploads, a domain with DNS, certificates, backups that have been restore-tested, and monitoring that alerts a person. Transactional email as soon as you send anything to users.',
      },
      {
        q: 'Can our developers still deploy themselves?',
        a: 'Yes. Deployment stays with your team. Everything below the application — servers, patching, certificates, backups, monitoring — is what we take on.',
      },
      {
        q: 'When is the right time to set this up?',
        a: 'Before launch. Certificates, DNS propagation and backup verification all take time you will not have on launch day, and the cost of doing it early is close to zero.',
      },
    ],
  },

  {
    slug: 'after-an-outage',
    eyebrow: 'Recovery',
    title: 'You found out from a customer',
    metaTitle: 'Website Went Down and Nobody Noticed',
    keywords: 'website down not notified, uptime monitoring, ssl certificate expired, why did my site go down, prevent website outage',
    description:
      'Something broke, nobody knew until someone complained, and the honest answer is that it could happen again tomorrow.',
    intro:
      'Almost every outage we have investigated began weeks earlier as something small that nothing was watching.',
    signs: [
      'The first report came from a customer, not a monitor',
      'It took a while to work out which part had actually failed',
      'Nobody is certain the same thing will not happen again',
      'The fix is done, but the reason it went unnoticed has not been addressed',
    ],
    firstWeek: [
      {
        title: 'We establish what really happened',
        body: 'Not who to blame — what the chain was. An expired certificate, a full disk and a failed backup have different causes and different fixes, and the visible symptom rarely names the right one.',
      },
      {
        title: 'We check whether it is still true',
        body: 'The same class of problem is usually present elsewhere. If one certificate lapsed, we check every certificate. If one backup failed, we test every restore.',
      },
      {
        title: 'We put watching in place',
        body: 'Expiry, renewals, disk, and availability, all checked continuously with alerts that reach a person. The point is not dashboards — it is that somebody is told.',
      },
      {
        title: 'We take over the routine',
        body: 'The work that prevents recurrence is unglamorous and constant. That is the part we do.',
      },
    ],
    risk: {
      title: 'Why the fix is not the fix',
      body: 'Repairing what broke restores service without changing the thing that let it go unnoticed for weeks. The next failure will be a different component and the same story.',
    },
    closing:
      'Tell us what happened. We will tell you what else is currently in the same state.',
    faqs: [
      {
        q: 'Why did our website go down without any warning?',
        a: 'Because nothing was watching. Most outages begin weeks earlier as something small — a certificate approaching expiry, a disk filling, a backup silently failing. Without a check that alerts a person, the first report comes from a customer.',
      },
      {
        q: 'My SSL certificate expired even though auto-renewal was on. How?',
        a: 'Automatic renewal attempts can fail quietly. Certbot retries twice a day, logs the failure and exits, and will do that for thirty days without notifying anyone. The usual causes are DNS having moved, a changed web-server config, or a duplicate certificate where the wrong one is being served.',
      },
      {
        q: 'How quickly should we find out about an outage?',
        a: 'Within minutes, from a monitor rather than a customer. Equally important is being warned before failure — an expiry date approaching is a problem you can fix calmly; an expired certificate is an incident.',
      },
      {
        q: 'We fixed what broke. Is that enough?',
        a: 'It restores service, but it does not change the reason nobody noticed for weeks. The next failure will be a different component and the same story unless something is watching.',
      },
    ],
  },
];

export function getUseCase(slug: string): UseCase | undefined {
  return useCases.find((useCase) => useCase.slug === slug);
}
